// One reference type card. Header is always visible (name, description,
// edit/delete); body collapses with the chevron and contains the drag/
// drop upload zone + image thumbnail grid. Match the Manus UX with
// app-native styling (bsp-card, bsp-btn, bsp-input, var(--bsp-*) tokens).
import { useState } from "react";
import { Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../../../lib/api";
import type { ReferenceImageDto, ReferenceTypeDto } from "./ReferenceLibrary";
import { ReferenceTypeForm } from "./ReferenceTypeForm";
import { ReferenceImageDropzone } from "./ReferenceImageDropzone";
import { ReferenceImageTile } from "./ReferenceImageTile";

interface Props {
  type: ReferenceTypeDto;
  onChanged: (next: ReferenceTypeDto) => void;
  onDeleted: (id: number) => void;
}

export function ReferenceTypeCard({ type, onChanged, onDeleted }: Props) {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (!window.confirm(`Delete type "${type.name}" and all its images?`)) return;
    try {
      await api(`/api/tools/pipe-takeoff/references/${type.id}`, { method: "DELETE" });
      onDeleted(type.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const addImage = (img: ReferenceImageDto) => {
    onChanged({ ...type, images: [...type.images, img] });
  };
  const updateImage = (img: ReferenceImageDto) => {
    onChanged({
      ...type,
      images: type.images.map((i) => (i.id === img.id ? img : i)),
    });
  };
  const removeImage = (id: number) => {
    onChanged({ ...type, images: type.images.filter((i) => i.id !== id) });
  };

  if (editing) {
    return (
      <ReferenceTypeForm
        initial={type}
        onSaved={(t) => { onChanged(t); setEditing(false); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="bsp-card" style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "space-between" }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            display: "flex", gap: 8, alignItems: "center",
            background: "transparent", border: 0, cursor: "pointer",
            padding: 0, color: "inherit", textAlign: "left", flex: 1, minWidth: 0,
          }}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong>{type.name}</strong>
              {!type.enabled && <span className="bsp-chip bsp-chip-grey">Disabled</span>}
              <span style={{ fontSize: 12, color: "var(--bsp-fg-muted)" }}>
                {type.images.length} image{type.images.length === 1 ? "" : "s"}
              </span>
            </div>
            {type.description && (
              <div style={{ fontSize: 12, color: "var(--bsp-fg-muted)", marginTop: 2 }}>
                {type.description}
              </div>
            )}
          </div>
        </button>
        <div style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
          <button type="button" className="bsp-btn bsp-btn-ghost bsp-btn-icon"
            onClick={() => setEditing(true)} title="Edit">
            <Pencil size={14} />
          </button>
          <button type="button" className="bsp-btn bsp-btn-ghost bsp-btn-icon"
            onClick={() => void remove()} title="Delete"
            style={{ color: "var(--bsp-red-fg)" }}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {error && (
        <p style={{ color: "var(--bsp-red-fg)", margin: 0, fontSize: 12 }}>{error}</p>
      )}

      {open && (
        <>
          <ReferenceImageDropzone typeId={type.id} onUploaded={addImage} />

          {type.images.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 12,
            }}>
              {type.images.map((img) => (
                <ReferenceImageTile
                  key={img.id}
                  typeId={type.id}
                  image={img}
                  onChanged={updateImage}
                  onDeleted={removeImage}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
